import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Download, FileText, AlertCircle } from "lucide-react";
import { shopCapture, apiError, fileUrl, formatMoney } from "@/lib/api";
import Seo from "@/components/Seo";

export default function CompraCompletada() {
  const [params] = useSearchParams();
  const pedido = params.get("pedido");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const done = useRef(false);

  useEffect(() => {
    if (done.current) { return; }
    done.current = true;
    if (!pedido) { setError("No hemos encontrado tu pedido."); return; }
    const tryCapture = (n) => shopCapture(pedido).then((o) => {
      if (o.status === "PAGADO") { setOrder(o); return; }
      if (n > 0) { setTimeout(() => tryCapture(n - 1), 2000); return; }
      setError("Tu pago aún no está confirmado. Si se ha completado, recibirás el libro por email en unos minutos.");
    }).catch((e) => setError(apiError(e)));
    tryCapture(10);
  }, [pedido]);

  return (
    <section className="max-w-2xl mx-auto px-4 sm:px-6 py-20 md:py-28" data-testid="purchase-page">
      <Seo title="Tu compra" description="Descarga de tu ebook" />
      {!order && !error && <p className="text-[#8a8a83]" data-testid="purchase-loading">Confirmando tu pago...</p>}
      {error && (
        <div data-testid="purchase-error">
          <AlertCircle className="text-red-600" size={32} />
          <h1 className="font-serif text-3xl text-[#0f2544] mt-4">No hemos podido completar la compra</h1>
          <p className="text-[#4a4a45] mt-3">{error}</p>
          <Link to="/contacto" className="hv-link-underline inline-block mt-6 text-[#1f44e0] font-medium">Contactar con la editorial</Link>
        </div>
      )}
      {order && (
        <div data-testid="purchase-success">
          <CheckCircle2 className="text-[#1f44e0]" size={36} />
          <p className="hv-overline mt-6">Pago confirmado · {order.numero_factura}</p>
          <h1 className="font-serif text-4xl md:text-5xl text-[#0f2544] mt-3 leading-tight">¡Gracias! Tu ebook ya es tuyo.</h1>
          <p className="text-[#4a4a45] mt-4"><strong>{order.titulo}</strong> · {formatMoney(order.total, order.moneda)}</p>
          <p className="text-[#4a4a45] mt-2">También te hemos enviado el enlace de descarga y tu factura a <strong>{order.email}</strong>.</p>
          <div className="flex flex-wrap gap-3 mt-8">
            {order.expirado || order.descargas_restantes === 0 ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-[#e7e5df] px-7 py-3.5 text-xs font-semibold tracking-[0.12em] text-[#8a8a83] cursor-not-allowed" data-testid="purchase-download-disabled">
                <Download size={15} /> {order.expirado ? "ENLACE CADUCADO" : "DESCARGAS AGOTADAS"}
              </span>
            ) : (
              <a href={fileUrl(order.download_url)} onClick={() => setTimeout(() => setOrder((o) => ({ ...o, descargas_restantes: Math.max(0, o.descargas_restantes - 1) })), 1500)} className="inline-flex items-center gap-2 rounded-full bg-[#1f44e0] px-7 py-3.5 text-xs font-semibold tracking-[0.12em] text-white hover:bg-[#0f2544] transition-colors" data-testid="purchase-download-btn">
                <Download size={15} /> DESCARGAR EPUB
              </a>
            )}
            <a href={fileUrl(order.invoice_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-[#0f2544]/25 px-7 py-3.5 text-xs font-semibold tracking-[0.12em] text-[#0f2544] hover:bg-[#0f2544] hover:text-white transition-colors" data-testid="purchase-invoice-btn">
              <FileText size={15} /> VER FACTURA
            </a>
          </div>
          <p className="text-xs text-[#8a8a83] mt-4" data-testid="purchase-download-info">Descargas disponibles: {order.descargas_restantes} de 2 · El enlace caduca el {order.caduca}. Guarda el archivo en tu dispositivo al descargarlo.</p>
          {(order.expirado || order.descargas_restantes === 0) && <p className="text-sm text-[#4a4a45] mt-2">Si necesitas volver a descargarlo, escríbenos desde la página de contacto indicando tu nº de factura.</p>}
        </div>
      )}
    </section>
  );
}
